'use client';

import { RewardThumbnail } from '@hyphengroup/cms-components/components/RewardThumbnail';
import { FC } from 'react';
import { getRewardSubtitle, getRewardTitle } from '../../utils';
import {
  CmsOffer,
  EnumFlexibleHeroBlockTheme,
} from '$/(cms)/_modules/cmsService/types';
import { FeCarousel } from '$/components/cms/components-internal/FeCarousel';
import {
  getUrgencyPhase,
  mapOfferToPeriodGifts,
} from '$/components/cms/helpers';

type RewardCarouselProps = {
  offers: CmsOffer[];
  lang: string;
  theme?: EnumFlexibleHeroBlockTheme;
};

export const RewardCarousel: FC<RewardCarouselProps> = ({
  offers,
  lang,
  theme,
}) => {
  const periodGifts = mapOfferToPeriodGifts(offers) ?? [];
  const shouldLoop = periodGifts.length > 1;

  if (periodGifts.length === 0) {
    return null;
  }

  return (
    <FeCarousel
      swiperCustomPagination
      swiperCustomNavigation
      // slide sizing, full-width track, arrows beside it, and the run-off fade
      // all live in ./rewardCarousel.css
      swiperClassNames="reward-carousel"
      swiperWrapperClassNames="w-full"
      swiperNestedClassNames="!h-auto"
      isDarkTheme={theme === EnumFlexibleHeroBlockTheme.Dark}
      swiperPropsOverride={{
        loop: false,
        pagination: true,
        navigation: true,
        centeredSlides: false,
        // 'auto' shrink-wraps each slide to the 120px tile so tiles sit a fixed
        // 24px apart, instead of `slidesPerView: N` splitting the row into equal
        // fractions and floating each tile in a wide slide (CMS-1856).
        slidesPerView: 'auto',
        // with few rewards the track is narrower than the row — center it
        // instead of leaving all the empty space on one side
        centerInsufficientSlides: true,
        autoplay: shouldLoop
          ? {
              delay: 4500,
              disableOnInteraction: false,
            }
          : false,
        spaceBetween: 24,
      }}
    >
      {periodGifts.map((reward) => (
        <RewardThumbnail
          key={reward!.uid ?? reward.gift_title}
          data-gift-uid={reward.uid}
          image={reward.desktop_image || reward.mobile_image}
          title={getRewardTitle(reward)}
          subtitle={getRewardSubtitle(reward)}
          tooltip={reward.additional_information}
          tagText={
            getUrgencyPhase(lang, reward.startDate, reward.endDate)?.value
          }
          // design-system Reward Thumbnail, "Small" variant (Figma 8556:13870)
          rewardThumbnailSizeClasses="h-[128px] w-[120px]"
        />
      ))}
    </FeCarousel>
  );
};
